import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth } from "../context/AuthContext";
import FrameworkWheel from "../components/FrameworkWheel";

export default function LoginPage() {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const { login } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const redirectTo = location.state?.from?.pathname || "/app/dashboard";

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        setLoading(true);
        const res = await login(email, password);
        setLoading(false);
        if (res.ok) {
            toast.success(`Welcome back, ${res.user.full_name || res.user.email}`);
            navigate(redirectTo, { replace: true });
        } else {
            setError(res.error);
        }
    };

    return (
        <div
            className="min-h-screen relative overflow-hidden bg-[#D6E6F2] text-[#0E1B2C] flex items-center justify-center px-6 py-12"
            data-testid="login-page"
        >
            {/* Decorative wheel motif in the corner */}
            <div
                aria-hidden
                className="pointer-events-none absolute -bottom-40 -left-40 opacity-40 hidden sm:block"
            >
                <FrameworkWheel size={520} />
            </div>
            <div
                aria-hidden
                className="pointer-events-none absolute top-0 right-0 w-[440px] h-[440px] rounded-full bg-[#8AC4FF]/30 blur-3xl"
            />

            <motion.div
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, ease: "easeOut" }}
                className="relative w-full max-w-md z-10"
            >
                <div className="mb-8">
                    <Link
                        to="/"
                        className="inline-flex items-center gap-3 text-[#0B2545] font-serif text-lg"
                        data-testid="login-brand-link"
                    >
                        <img
                            src="/esss-logo.png?v=3"
                            alt="ESSS"
                            className="h-10 w-10 rounded-md object-contain bg-white"
                            data-testid="login-logo"
                        />
                        <span className="font-semibold">ESSS Coherence</span>
                    </Link>
                </div>

                <div className="rounded-2xl bg-white/95 backdrop-blur-md border border-white shadow-xl shadow-[#0B2545]/10 p-8">
                    <div className="text-xs uppercase tracking-[0.25em] font-bold text-[#1E56A0] mb-3">
                        Secure sign in
                    </div>
                    <h1 className="font-serif text-3xl sm:text-4xl font-semibold text-[#0B2545] leading-tight mb-2">
                        Welcome back.
                    </h1>
                    <p className="text-[#0E1B2C]/70 mb-8 text-sm leading-relaxed">
                        Sign in with the credentials your platform administrator provided.
                    </p>

                    <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
                        <div className="space-y-2">
                            <Label htmlFor="login-email" className="text-[#0B2545]">
                                Email
                            </Label>
                            <Input
                                id="login-email"
                                type="email"
                                autoComplete="email"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="you@district.org"
                                className="h-11 bg-white border-[#D6E6F2] focus-visible:ring-[#1E56A0]"
                                data-testid="login-email-input"
                            />
                        </div>
                        <div className="space-y-2">
                            <div className="flex items-center justify-between">
                                <Label htmlFor="login-password" className="text-[#0B2545]">
                                    Password
                                </Label>
                                <button
                                    type="button"
                                    className="text-xs text-[#1E56A0] hover:underline"
                                    onClick={() =>
                                        toast.info("Contact your administrator to reset your password.")
                                    }
                                    data-testid="login-forgot-password-btn"
                                >
                                    Forgot password?
                                </button>
                            </div>
                            <Input
                                id="login-password"
                                type="password"
                                autoComplete="current-password"
                                required
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="••••••••"
                                className="h-11 bg-white border-[#D6E6F2] focus-visible:ring-[#1E56A0]"
                                data-testid="login-password-input"
                            />
                        </div>

                        {error && (
                            <div
                                className="text-sm rounded-md bg-red-50 border border-red-200 text-red-700 px-3 py-2"
                                data-testid="login-error"
                            >
                                {error}
                            </div>
                        )}

                        <Button
                            type="submit"
                            disabled={loading}
                            className="w-full h-11 rounded-full bg-[#0B2545] hover:bg-[#1E56A0] text-white font-semibold text-sm"
                            data-testid="login-submit-btn"
                        >
                            {loading ? (
                                <>
                                    <Loader2 className="animate-spin" size={16} />
                                    Signing in…
                                </>
                            ) : (
                                <>
                                    Sign in
                                    <ArrowRight size={16} />
                                </>
                            )}
                        </Button>
                    </form>
                </div>

                <p className="mt-6 text-xs text-[#0E1B2C]/60 text-center">
                    ESSS Coherence is invitation-only. Contact your platform admin for access.
                </p>
            </motion.div>
        </div>
    );
}
